import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { RestService } from '../../services/rest.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { BrandService } from '../../../services/brand-config.service';
import { PlatformService } from '../../services/platform.service';
import { CookieConsentComponent, readFlag } from '../../cookie-consent/cookie-consent.component';

interface ConsentRow {
  key: string;
  i18n: string;
  /** Withdrawing it pauses new checks (server 403 on scan/create). */
  required: boolean;
}

const ROWS: ConsentRow[] = [
  { key: 'camera.required', i18n: 'cameraRequired', required: true },
  { key: 'health.collect', i18n: 'healthCollect', required: true },
  { key: 'camera.dataImprovement', i18n: 'dataImprovement', required: false },
  { key: 'camera.dataSharing', i18n: 'dataSharing', required: false },
  { key: 'camera.tracking', i18n: 'tracking', required: false },
  // Separate marketing opt-in (GTM / Meta pixel / Google Ads), never bundled with analytics.
  { key: 'tracking.marketing', i18n: 'marketing', required: false },
  { key: 'ai.gemini', i18n: 'aiGemini', required: false },
];

/** Ledger key -> the browser cookie flag it drives. */
const COOKIE_FLAG: Record<string, 'cookieConsent' | 'cookieConsentMarketing'> = {
  'camera.tracking': 'cookieConsent',
  'tracking.marketing': 'cookieConsentMarketing',
};

/**
 * Profile > Privacy & consents (V-12 / V-13 / V-16): her current consent state
 * from the append-only ledger, toggles that append granted / withdrawn events,
 * a link that reopens the cookie banner, and a download of all her data.
 */
@Component({
  selector: 'app-privacy-settings',
  standalone: true,
  imports: [NgIf, NgFor, TranslatePipe],
  templateUrl: './privacy-settings.component.html',
  styleUrls: ['./privacy-settings.component.scss'],
})
export class PrivacySettingsComponent implements OnInit {
  /** Parent runs its existing account-deletion flow. */
  @Output() requestDeletion = new EventEmitter<void>();

  private rest = inject(RestService);
  private translation = inject(TranslationService);
  private brand = inject(BrandService);
  private platform = inject(PlatformService);

  rows = ROWS;
  state: Record<string, string> = {};
  loading = true;
  loadError = false;
  busyKey: string | null = null;
  saveError = '';
  exportBusy = false;
  exportError = '';

  get isWeb(): boolean {
    return this.platform.isWeb;
  }

  /** A required consent is currently withdrawn: new checks are paused. */
  get blocked(): boolean {
    return ROWS.some((r) => r.required && this.state[r.key] === 'withdrawn');
  }

  isOn(key: string): boolean {
    const flag = COOKIE_FLAG[key];
    if (flag && this.state[key] === undefined) return readFlag(flag);
    return this.state[key] === 'granted';
  }

  async ngOnInit(): Promise<void> {
    await this.load();
    // Deep link from the "new checks are paused" notice (#privacy).
    if (location.hash === '#privacy') {
      setTimeout(() => document.getElementById('privacy')?.scrollIntoView({ behavior: 'smooth' }), 0);
    }
  }

  private apply(res: any): void {
    const next: Record<string, string> = {};
    for (const c of res?.consents || []) next[c.key] = c.status;
    this.state = next;
  }

  async load(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    try {
      const res: any = await this.rest.request({
        path: 'user/me/consents',
        method: 'GET',
        throwError: true,
        skipAuthRedirect: true,
      });
      this.apply(res);
    } catch {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  async toggle(row: ConsentRow): Promise<void> {
    if (this.busyKey) return;
    const turnOn = !this.isOn(row.key);
    if (!turnOn && row.required && !confirm(this.translation.translate('privacySettings.withdrawConfirm'))) {
      return;
    }
    // Withdrawing Gemini consent means the next voice note / meal asks again.
    if (row.key === 'ai.gemini' && !turnOn) localStorage.removeItem('mk_ai_gemini_ok');
    this.busyKey = row.key;
    this.saveError = '';
    try {
      const t = (k: string) => this.translation.translate(k);
      const res: any = await this.rest.request({
        path: 'user/me/consents',
        method: 'POST',
        throwError: true,
        skipAuthRedirect: true,
        body: {
          key: row.key,
          status: turnOn ? 'granted' : 'withdrawn',
          text: `${t('privacySettings.item.' + row.i18n)}: ${t('privacySettings.item.' + row.i18n + 'Desc')}`,
          locale: this.translation.getCurrentLanguage(),
          brand: this.brand.config.brandName,
        },
      });
      this.apply(res);
      if (COOKIE_FLAG[row.key]) {
        const fn = (window as any).applyTrackingConsent;
        const analytics = row.key === 'camera.tracking' ? turnOn : readFlag('cookieConsent');
        const marketing = row.key === 'tracking.marketing' ? turnOn : readFlag('cookieConsentMarketing');
        if (typeof fn === 'function') fn({ analytics, marketing });
      }
    } catch {
      this.saveError = this.translation.translate('privacySettings.saveError');
    } finally {
      this.busyKey = null;
    }
  }

  openCookieSettings(): void {
    window.dispatchEvent(new Event(CookieConsentComponent.REOPEN_EVENT));
  }

  async downloadMyData(): Promise<void> {
    if (this.exportBusy) return;
    this.exportBusy = true;
    this.exportError = '';
    try {
      const data = await this.rest.request<any>({
        path: 'user/me/export',
        method: 'GET',
        throwError: true,
        skipAuthRedirect: true,
      });
      const name = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const file = typeof File !== 'undefined' ? new File([blob], name, { type: 'application/json' }) : null;
      const nav: any = navigator;
      if (this.platform.isNative && file && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: name });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        this.exportError = this.translation.translate('privacySettings.exportError');
      }
    } finally {
      this.exportBusy = false;
    }
  }
}
